import React, { FC, ReactNode, useEffect } from 'react'
import {
  Button,
  ModalActions,
  ModalContent,
  ModalHeader,
} from '@lightspeed/design-system-react'
import { VendImage } from '@vend/react-utilities'
import { ApiRetailerPaymentType, ApiTransaction } from '@vend/types'
import VendNumber from 'vend-number'
import { LSPAYModalContent } from '../modal-states/LSPAYModalContent'
import { ErrorContent } from '../modal-states/ErrorContent'
import { useRefundEffectReducer } from '../../hooks/use-refund-effect-reducer-legacy/useRefundEffectReducer'
import { ConnectingToTerminal } from '../modal-states/ConnectingToTerminal'
import { CheckingPairing } from '../modal-states/CheckingPairing'
import { Processing } from '../modal-states/Processing'
import { Register } from '../../helpers/lspay.types'

interface LSPAYReferencedRefundOnlyProps {
  register: Register
  retailerPaymentType: ApiRetailerPaymentType
  originalRegisterSalePaymentId: string
  outletId: string
  amount: VendNumber
  transactionId: string
  currency: string
  onSuccess: (transaction: Promise<ApiTransaction>) => void
  onError: () => void
}

export const LSPAYReferencedRefundOnly: FC<LSPAYReferencedRefundOnlyProps> = ({
  register,
  retailerPaymentType,
  originalRegisterSalePaymentId,
  outletId,
  amount,
  transactionId,
  currency,
  onSuccess,
  onError,
}) => {
  const [refundState, dispatch] = useRefundEffectReducer({
    amount,
    currency,
    originalRegisterSalePaymentId,
    outletId,
    register,
    retailerPaymentType,
    transactionId,
  })

  useEffect(() => {
    if (refundState.status === 'done') {
      onSuccess(Promise.resolve(refundState.transaction))
    }
  }, [onSuccess, refundState])

  const getRefundContent = (): ReactNode => {
    switch (refundState.status) {
      case 'interacPresentPrompt': {
        return (
          <>
            <ModalHeader>
              The refund needs a card present to continue.
            </ModalHeader>
            <ModalContent>
              <p className="vd-p">
                Due to the type of card that the customer used in the original
                sale, the same card is required to be presented to process the
                refund. You can continue or choose another refund type.
              </p>
            </ModalContent>
            <ModalActions>
              <div className="vd-btn-group">
                <Button
                  data-track="choose-another-refund-type"
                  variant="supplementary"
                  onClick={() => onError()}
                >
                  Choose Another Refund Type
                </Button>
                <Button
                  variant="go"
                  onClick={() => dispatch('CONTINUE_WITH_CARD_PRESENT')}
                  data-track="continue-with-card-present"
                >
                  Continue
                </Button>
              </div>
            </ModalActions>
          </>
        )
      }
      case 'fetchingPairing': {
        return <CheckingPairing />
      }
      case 'connectingToTerminal':
        return <ConnectingToTerminal />
      case 'refundingViaCardPresent': {
        let modalActions = null
        if (refundState.status === 'refundingViaCardPresent') {
          modalActions = (
            <Button
              variant="no"
              className="vd-mr2"
              onClick={() => {
                refundState.terminal
                  .cancelCollectRefundPaymentMethod()
                  .catch(() => {
                    // absorb this error, there's nothing to do about it
                  })
              }}
              data-track="cancel-refund"
            >
              Cancel Refund
            </Button>
          )
        }

        return (
          <LSPAYModalContent actions={modalActions}>
            <VendImage
              relativeImagePath="payments/insert-card-v1.svg"
              relativeDarkImagePath="payments/insert-card-darkmode-v1.svg"
              className="vd-mb2"
            />
            <h2 className="vd-align-center">
              Tap, insert, or swipe the customer's card
              <br />
              used in the original sale.
            </h2>
          </LSPAYModalContent>
        )
      }
      case 'persistingCardPresentRefund':
      case 'refundingViaCardNotPresent': {
        return <Processing isRefund={true} />
      }
      case 'done': {
        // Modal should be closed if the state was done.
        // Hence we can just return
        return
      }
      default: {
        // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
        const _exhaustiveCheck: never = refundState
        return _exhaustiveCheck
      }
    }
  }

  return (
    <>
      {refundState.errorMessage ? (
        <ErrorContent
          lspayError={refundState.errorMessage}
          disableRetry={!refundState.retryable}
          retry={() => dispatch('RETRY')}
          close={() => {
            onError()
          }}
        />
      ) : (
        getRefundContent()
      )}
    </>
  )
}
